import { useField } from 'formik';
import ValidationError from 'gcs-common/components/Formik/ValidationError/ValidationError';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import errorIcon from '../../img/close_red.svg';
import selectCustomStyles from './customStyles';
import styles from './styles.module.scss';

const FormikSelect = ({
  name = '',
  options: providedOptions,
  label = '',
  disabled = false,
  placeholder = 'Suchen...',
  labelKey,
  valueKey,
  isMulti = false,
  isLoading = false,
  onChange,
  onInputChange,
  initialValue,
  hasError = false,
  localFilter,
  isClearable = true,
  compareOptionsFunction,
  getOptionLabel,
  formatOptionLabel,
  optionBackgroundColor,
  optionsWidth,
  customFilter,
}) => {
  const [field, , helpers] = useField(name);
  const selectRef = useRef(null);
  // Note: We cache previously selected options, otherwise they will be removed from the select
  const currentlySelectedCache = useRef(
    Array.isArray(initialValue) ? initialValue : initialValue ? [initialValue] : [],
  );

  // Note: To display the currently selected entity if its load async
  let options = [...providedOptions];
  if (currentlySelectedCache.current && currentlySelectedCache.current.length > 0 && isMulti) {
    options = [...currentlySelectedCache.current, ...options];
  }

  const handleChange = useCallback(
    (option) => {
      let value;
      if (isMulti) {
        // biome-ignore lint/complexity/useOptionalChain: <explanation>
        value = option && option.map((op) => (valueKey ? op[valueKey] : op));
        currentlySelectedCache.current = option;
        if (!value) {
          value = [];
        }
      } else {
        value = option && (valueKey ? option[valueKey] : option);
        currentlySelectedCache.current = option ? [option] : [];
      }
      helpers.setValue(value);
      // biome-ignore lint/complexity/useOptionalChain: <explanation>
      onChange && onChange(value);
    },
    [helpers, isMulti, onChange, valueKey],
  );

  const [handleInputChangedDebounced] = useDebouncedCallback((inputValue) => {
    if (onInputChange && (isMulti || (!isMulti && inputValue !== ''))) {
      onInputChange(inputValue);
    }
  }, 300);

  const areOptionsEqual = useCallback(
    (value, option) => {
      if (compareOptionsFunction) {
        return compareOptionsFunction(value, option);
      }
      return value === option;
    },
    [compareOptionsFunction],
  );

  const value = useMemo(() => {
    if (isMulti) {
      return (
        // biome-ignore lint/complexity/useOptionalChain: <explanation>
        field.value &&
        field.value.map((val) => (options ? options.find((option) => areOptionsEqual(option[valueKey], val)) : ''))
      );
    }
    return options
      ? options.find((option) =>
          // biome-ignore lint/complexity/useOptionalChain: <explanation>
          areOptionsEqual(option && option[valueKey], field.value),
        )
      : '';
  }, [field.value, isMulti, options, valueKey, areOptionsEqual]);

  const filteredOptions = useMemo(() => {
    if (localFilter) {
      return options.filter((a) => !localFilter.includes(a[valueKey]));
    }
    return options;
  }, [localFilter, options, valueKey]);

  useEffect(() => {
    if (!disabled) {
      // biome-ignore lint/complexity/useOptionalChain: <explanation>
      onInputChange && onInputChange('');
    }
  }, [disabled]);

  return (
    <div className={styles.formikSelectContainer}>
      <div className={`${styles.formikSelectWrapper} ${disabled ? styles.disabled : ''}`}>
        {label && (
          <label className={styles.formikSelectLabel} htmlFor={field.name}>
            {label}
          </label>
        )}
        {hasError && disabled ? (
          <div className={styles.formikSelectError}>
            <img src={errorIcon} alt="Fehler" />
            Fehler
          </div>
        ) : (
          <div ref={selectRef}>
            <Select
              onInputChange={handleInputChangedDebounced}
              styles={selectCustomStyles}
              isDisabled={disabled}
              placeholder={disabled ? '-' : placeholder}
              options={filteredOptions} // the options that can be selected
              name={field.name}
              getOptionValue={(option) => (valueKey ? option[valueKey] : option)}
              getOptionLabel={getOptionLabel || ((option) => (labelKey ? option[labelKey] : option))}
              formatOptionLabel={formatOptionLabel}
              value={value || null}
              filterOption={customFilter}
              onChange={handleChange}
              onBlur={field.onBlur}
              menuPlacement="auto"
              isMulti={isMulti}
              isLoading={isLoading}
              noOptionsMessage={() => 'Keine Optionen'}
              loadingMessage={() => 'Lädt...'}
              menuPortalTarget={document.body}
              isClearable={isClearable}
              optionBackgroundColor={optionBackgroundColor}
              optionsWidth={optionsWidth}
              closeMenuOnScroll={(e) => e.target.contains(selectRef.current)}
            />
          </div>
        )}
      </div>
      <ValidationError name={name} />
    </div>
  );
};

FormikSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  initialValue: PropTypes.any,
  localFilter: PropTypes.arrayOf(PropTypes.shape),
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onChange: PropTypes.func,
  isClearable: PropTypes.bool,
  onInputChange: PropTypes.func,
  compareOptionsFunction: PropTypes.func,
  getOptionLabel: PropTypes.func,
  optionBackgroundColor: PropTypes.string,
  optionsWidth: PropTypes.string,
  customFilter: PropTypes.func,
  formatOptionLabel: PropTypes.func,
};

export default FormikSelect;
